import type { OwnerBookingWithTypeDto } from '@shared/core/bookings'
import ms from 'ms'
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHandleRetry } from 'src/modules/core/hooks/use-handle-retry'
import { BookingsService } from 'src/services/bookings.service'

export const GET_BOOKING_BY_EVENT_ID_CACHE_KEY = 'get-booking-by-event-id'

type BookingByEventId = OwnerBookingWithTypeDto | null

const NOT_FOUND_STATUS = 404

// Axios rejects a 404, which is expected here (not linked by sync yet, or cancelled): map it to null.
async function fetchBookingByEventId(eventId: string): Promise<BookingByEventId> {
  try {
    return await BookingsService.getBookingByEventId(eventId)
  } catch (error) {
    if (error?.response?.status === NOT_FOUND_STATUS) return null
    throw error
  }
}

/** Disabled unless the caller passes `enabled: true`: each read costs a Google userinfo call. */
export function useBookingByEventId(eventId: string, options: UseQueryOptions<BookingByEventId> = {}) {
  const handleRetry = useHandleRetry()
  const queryClient = useQueryClient()
  const queryKey = [GET_BOOKING_BY_EVENT_ID_CACHE_KEY, eventId]
  // Sync may link the booking any moment, so a cached 404 is refetched on mount.
  const isCachedNotFound = queryClient.getQueryData<BookingByEventId>(queryKey) === null
  return useQuery(queryKey, () => fetchBookingByEventId(eventId), {
    staleTime: isCachedNotFound ? 0 : ms('5 minutes'),
    refetchOnWindowFocus: false,
    retry: handleRetry,
    ...options,
    enabled: Boolean(eventId) && options.enabled === true,
  })
}
